import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { adminService } from '../services/adminService';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Modal from '../components/common/Modal';

const COMMON_TIMEZONES = [
  { value: 'Asia/Kolkata',        label: 'IST — India Standard Time (UTC+5:30)' },
  { value: 'America/Los_Angeles', label: 'PST/PDT — Pacific Time (UTC-8/-7)' },
  { value: 'America/New_York',    label: 'EST/EDT — Eastern Time (UTC-5/-4)' }
];

const AdminPage = () => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Admin Panel</h1>
      
      <div className="flex gap-4 mb-6 border-b">
        <NavLink
          to="/admin/users"
          className={({ isActive }) =>
            `px-4 py-2 font-medium ${isActive ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`
          }
        >
          Users
        </NavLink>
        <NavLink
          to="/admin/clients"
          className={({ isActive }) =>
            `px-4 py-2 font-medium ${isActive ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`
          }
        >
          Clients
        </NavLink>
        <NavLink
          to="/admin/departments"
          className={({ isActive }) =>
            `px-4 py-2 font-medium ${isActive ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`
          }
        >
          Departments
        </NavLink>
        <NavLink
          to="/admin/categories"
          className={({ isActive }) =>
            `px-4 py-2 font-medium ${isActive ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`
          }
        >
          Categories
        </NavLink>
      </div>

      <Routes>
        <Route path="users" element={<UsersManagement />} />
        <Route path="clients" element={<ClientsManagement />} />
        <Route path="departments" element={<DepartmentsManagement />} />
        <Route path="categories" element={<CategoriesManagement />} />
        <Route index element={<UsersManagement />} />
      </Routes>
    </div>
  );
};

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [initialSelectedUserSnapshot, setInitialSelectedUserSnapshot] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: 'Welcome@123',
    firstName: '',
    lastName: '',
    departmentId: '',
    managerId: '',
    role: 'Employee'
  });

  const managerOptions = users
    .filter((user) => (user.roles || []).some((role) => ['Manager', 'Admin', 'Executive'].includes(role)))
    .map((user) => ({
      value: user.id,
      label: user.fullName
    }));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, deptRes] = await Promise.all([
        adminService.getAllUsers(),
        adminService.getDepartments()
      ]);
      setUsers(usersRes.data || []);
      setDepartments(deptRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser(newUser);
      setShowModal(false);
      setNewUser({
        email: '',
        password: 'Welcome@123',
        firstName: '',
        lastName: '',
        departmentId: '',
        managerId: '',
        role: 'Employee'
      });
      alert('User created successfully! Default password: Welcome@123');
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleOpenEdit = (user) => {
    const payload = {
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      departmentId: user.departmentId || '',
      managerId: user.manager?.id || user.managerId || '',
      role: user.roles?.[0] || 'Employee',
      timezone: user.timezone || 'Asia/Kolkata',
      password: ''
    };
    setSelectedUser(payload);
    setInitialSelectedUserSnapshot(JSON.stringify(payload));
    setShowEditModal(true);
  };

  const closeUserEditModal = () => {
    if (selectedUser) {
      const currentSnapshot = JSON.stringify(selectedUser);
      if (initialSelectedUserSnapshot && currentSnapshot !== initialSelectedUserSnapshot) {
        const confirmClose = window.confirm('You have unsaved changes. Discard them?');
        if (!confirmClose) return;
      }
    }
    setShowEditModal(false);
    setSelectedUser(null);
    setInitialSelectedUserSnapshot(null);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        departmentId: selectedUser.departmentId || null,
        managerId: selectedUser.managerId || null,
        role: selectedUser.role,
        timezone: selectedUser.timezone
      };
      if (selectedUser.password) {
        payload.password = selectedUser.password;
      }
      await adminService.updateUser(selectedUser.id, payload);
      closeUserEditModal();
      loadData();
      alert('User updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    const confirmDelete = window.confirm('Are you sure you want to deactivate this user?');
    if (!confirmDelete) return;
    try {
      await adminService.deleteUser(selectedUser.id);
      closeUserEditModal();
      loadData();
      alert('User deactivated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete user');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Users</h2>
          <p className="text-sm text-gray-600">Create and manage user accounts</p>
        </div>
        <Button onClick={() => setShowModal(true)}>Create User</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timezone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">{user.fullName}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.department?.name || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.manager?.name || '-'}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-1 flex-wrap">
                    {user.roles && user.roles.map(role => (
                      <span key={role} className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                        {role}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.timezone || 'Asia/Kolkata'}</td>
                <td className="px-6 py-4">
                  <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(user)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Create New User">
        <form onSubmit={handleCreate}>
          <Input
            label="First Name"
            value={newUser.firstName}
            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
            required
          />
          <Input
            label="Last Name"
            value={newUser.lastName}
            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            required
          />
          <Input
            label="Default Password"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            required
          />
          <Select
            label="Department"
            value={newUser.departmentId}
            onChange={(e) => setNewUser({ ...newUser, departmentId: e.target.value })}
            options={departments.map(d => ({ value: d.id, label: d.name }))}
            placeholder="Select department"
          />
          <Select
            label="Role"
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            options={['Employee', 'Manager', 'Admin', 'Executive'].map(role => ({ value: role, label: role }))}
            required
          />

          <div className="mt-4 p-4 bg-blue-50 rounded">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The user will be created with the default password shown above.
              They should change it after first login. Managers can then assign this user to their team.
            </p>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">Create User</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={closeUserEditModal}
        title="Edit User"
      >
        {selectedUser && (
          <form onSubmit={handleUpdateUser}>
            <Input
              label="Name"
              value={selectedUser.fullName}
              disabled
              readOnly
            />
            <Input
              label="Email"
              value={selectedUser.email}
              disabled
              readOnly
            />
            <Select
              label="Department"
            value={selectedUser.departmentId}
            onChange={(e) => setSelectedUser({ ...selectedUser, departmentId: e.target.value })}
            options={departments.map(d => ({ value: d.id, label: d.name }))}
            placeholder="Select department"
            />
            <Select
              label="Manager"
              value={selectedUser.managerId}
              onChange={(e) => setSelectedUser({ ...selectedUser, managerId: e.target.value })}
              options={managerOptions.filter((option) => String(option.value) !== String(selectedUser.id))}
              placeholder="Select manager"
            />
            <Select
              label="Role"
              value={selectedUser.role}
              onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
              options={['Employee', 'Manager', 'Admin', 'Executive'].map(role => ({ value: role, label: role }))}
              required
            />
            <Select
              label="Timezone"
              value={selectedUser.timezone}
              onChange={(e) => setSelectedUser({ ...selectedUser, timezone: e.target.value })}
              options={COMMON_TIMEZONES}
              required
            />

            <Input
              label="New Password (Optional)"
              type="password"
              value={selectedUser.password}
              onChange={(e) => setSelectedUser({ ...selectedUser, password: e.target.value })}
              placeholder="Leave blank to keep current password"
            />

            <div className="mt-4 flex justify-between gap-3">
              <Button type="button" variant="danger" onClick={handleDeleteUser}>
                Delete User
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={() => {
                  closeUserEditModal();
                }}>
                  Cancel
                </Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [initialClientSnapshot, setInitialClientSnapshot] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', description: '' });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    const response = await adminService.getClients();
    setClients(response.data || []);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await adminService.createClient(newClient);
    setShowModal(false);
    setNewClient({ name: '', description: '' });
    loadClients();
  };

  const handleOpenEdit = (client) => {
    const payload = {
      id: client.id,
      name: client.name || '',
      description: client.description || ''
    };
    setEditingClient(payload);
    setInitialClientSnapshot(JSON.stringify(payload));
    setShowEditModal(true);
  };

  const closeClientEditModal = () => {
    if (editingClient && initialClientSnapshot && JSON.stringify(editingClient) !== initialClientSnapshot) {
      const confirmClose = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmClose) return;
    }
    setShowEditModal(false);
    setEditingClient(null);
    setInitialClientSnapshot(null);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    await adminService.updateClient(editingClient.id, {
      name: editingClient.name,
      description: editingClient.description
    });
    closeClientEditModal();
    loadClients();
  };

  const handleRemoveClient = async () => {
    if (!editingClient) return;
    const confirmDelete = window.confirm('Remove this client?');
    if (!confirmDelete) return;
    await adminService.deleteClient(editingClient.id);
    closeClientEditModal();
    loadClients();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Clients</h2>
        <Button onClick={() => setShowModal(true)}>Add Client</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.map(client => (
              <tr key={client.id}>
                <td className="px-6 py-4">{client.name}</td>
                <td className="px-6 py-4">{client.description || '-'}</td>
                <td className="px-6 py-4">
                  <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(client)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Client">
        <form onSubmit={handleCreate}>
          <Input
            label="Client Name"
            value={newClient.name}
            onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
            required
          />
          <Input
            label="Description"
            value={newClient.description}
            onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
          />
          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">Create</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={closeClientEditModal}
        title="Edit Client"
      >
        {editingClient && (
          <form onSubmit={handleUpdateClient}>
            <Input
              label="Client Name"
              value={editingClient.name}
              onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              value={editingClient.description}
              onChange={(e) => setEditingClient({ ...editingClient, description: e.target.value })}
            />
            <div className="mt-4 flex justify-between gap-3">
              <Button type="button" variant="danger" onClick={handleRemoveClient}>
                Remove
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={() => {
                  closeClientEditModal();
                }}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

const DepartmentsManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [initialDepartmentSnapshot, setInitialDepartmentSnapshot] = useState(null);
  const [newDepartment, setNewDepartment] = useState({ name: '', description: '' });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    const response = await adminService.getDepartments();
    setDepartments(response.data || []);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await adminService.createDepartment(newDepartment);
    setShowModal(false);
    setNewDepartment({ name: '', description: '' });
    loadDepartments();
  };

  const handleOpenEdit = (department) => {
    const payload = {
      id: department.id,
      name: department.name || '',
      description: department.description || ''
    };
    setEditingDepartment(payload);
    setInitialDepartmentSnapshot(JSON.stringify(payload));
    setShowEditModal(true);
  };

  const closeDepartmentEditModal = () => {
    if (editingDepartment && initialDepartmentSnapshot && JSON.stringify(editingDepartment) !== initialDepartmentSnapshot) {
      const confirmClose = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmClose) return;
    }
    setShowEditModal(false);
    setEditingDepartment(null);
    setInitialDepartmentSnapshot(null);
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    await adminService.updateDepartment(editingDepartment.id, {
      name: editingDepartment.name,
      description: editingDepartment.description
    });
    closeDepartmentEditModal();
    loadDepartments();
  };

  const handleRemoveDepartment = async () => {
    if (!editingDepartment) return;
    const confirmDelete = window.confirm('Remove this department?');
    if (!confirmDelete) return;
    await adminService.deleteDepartment(editingDepartment.id);
    closeDepartmentEditModal();
    loadDepartments();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Departments</h2>
        <Button onClick={() => setShowModal(true)}>Add Department</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {departments.map(dept => (
              <tr key={dept.id}>
                <td className="px-6 py-4">{dept.name}</td>
                <td className="px-6 py-4">{dept.description || '-'}</td>
                <td className="px-6 py-4">
                  <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(dept)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Department">
        <form onSubmit={handleCreate}>
          <Input
            label="Department Name"
            value={newDepartment.name}
            onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
            required
          />
          <Input
            label="Description"
            value={newDepartment.description}
            onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
          />
          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">Create</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={closeDepartmentEditModal}
        title="Edit Department"
      >
        {editingDepartment && (
          <form onSubmit={handleUpdateDepartment}>
            <Input
              label="Department Name"
              value={editingDepartment.name}
              onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              value={editingDepartment.description}
              onChange={(e) => setEditingDepartment({ ...editingDepartment, description: e.target.value })}
            />
            <div className="mt-4 flex justify-between gap-3">
              <Button type="button" variant="danger" onClick={handleRemoveDepartment}>
                Remove
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={() => {
                  closeDepartmentEditModal();
                }}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [initialCategorySnapshot, setInitialCategorySnapshot] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const response = await adminService.getCategories();
    setCategories(response.data || []);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await adminService.createCategory(newCategory);
    setShowModal(false);
    setNewCategory({ name: '', description: '' });
    loadCategories();
  };

  const handleOpenEdit = (category) => {
    const payload = {
      id: category.id,
      name: category.name || '',
      description: category.description || ''
    };
    setEditingCategory(payload);
    setInitialCategorySnapshot(JSON.stringify(payload));
    setShowEditModal(true);
  };

  const closeCategoryEditModal = () => {
    if (editingCategory && initialCategorySnapshot && JSON.stringify(editingCategory) !== initialCategorySnapshot) {
      const confirmClose = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmClose) return;
    }
    setShowEditModal(false);
    setEditingCategory(null);
    setInitialCategorySnapshot(null);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    await adminService.updateCategory(editingCategory.id, {
      name: editingCategory.name,
      description: editingCategory.description
    });
    closeCategoryEditModal();
    loadCategories();
  };

  const handleRemoveCategory = async () => {
    if (!editingCategory) return;
    const confirmDelete = window.confirm('Remove this category?');
    if (!confirmDelete) return;
    await adminService.deleteCategory(editingCategory.id);
    closeCategoryEditModal();
    loadCategories();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Categories</h2>
        <Button onClick={() => setShowModal(true)}>Add Category</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map(cat => (
              <tr key={cat.id}>
                <td className="px-6 py-4">{cat.name}</td>
                <td className="px-6 py-4">{cat.description || '-'}</td>
                <td className="px-6 py-4">
                  <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(cat)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Category">
        <form onSubmit={handleCreate}>
          <Input
            label="Category Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            required
          />
          <Input
            label="Description"
            value={newCategory.description}
            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
          />
          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">Create</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={closeCategoryEditModal}
        title="Edit Category"
      >
        {editingCategory && (
          <form onSubmit={handleUpdateCategory}>
            <Input
              label="Category Name"
              value={editingCategory.name}
              onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              value={editingCategory.description}
              onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
            />
            <div className="mt-4 flex justify-between gap-3">
              <Button type="button" variant="danger" onClick={handleRemoveCategory}>
                Remove
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={() => {
                  closeCategoryEditModal();
                }}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminPage;
